/**
 * Dashboard appearance — light-default theme with an opt-in dark variant.
 *
 * The app ships a light "Verdikt Daylight" theme as the default and retains
 * the graphite dark theme as an opt-in variant. Colour is driven two ways that
 * both flip together: CSS custom properties in `index.css` (`:root` = light,
 * `html.dark` = graphite) and Tailwind `dark:` variants gated on the `dark`
 * class on `<html>`. `applyThemeToDocument` adds/removes that class so both
 * systems switch in lock-step. Preference is persisted for back-compat with
 * the admin preferences API (`patchAdminPreferences` / `user.themePreference`).
 */

export const THEME_STORAGE_KEY = "identity_armour_theme";

export const THEME_CHANGED = "identity-armour:theme-changed";

export type ThemePreference = "light" | "dark" | "system";

/**
 * Returns whether `document.documentElement` should carry the `dark` class for
 * a given preference. `system` follows the OS `prefers-color-scheme`.
 */
export function resolveDarkClass(theme: ThemePreference, prefersDark: boolean): boolean {
  if (theme === "dark") return true;
  if (theme === "system") return prefersDark;
  return false;
}

/** Reads the OS dark-mode preference; false when unavailable (SSR/tests). */
function systemPrefersDark(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/**
 * Reads the stored preference. Defaults to `light` when unset or invalid.
 */
export function getThemePreference(): ThemePreference {
  if (typeof localStorage === "undefined") return "light";
  const raw = localStorage.getItem(THEME_STORAGE_KEY);
  const valid: ThemePreference[] = ["light", "dark", "system"];
  if (raw && valid.includes(raw as ThemePreference)) {
    return raw as ThemePreference;
  }
  return "light";
}

/**
 * Applies (or removes) the `dark` class on `<html>` to match the preference.
 */
export function applyThemeToDocument(theme: ThemePreference): void {
  if (typeof document === "undefined") return;
  const dark = resolveDarkClass(theme, systemPrefersDark());
  document.documentElement.classList.toggle("dark", dark);
}

/**
 * Persists the preference and applies it to the document.
 */
export function setThemePreference(theme: ThemePreference): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(THEME_STORAGE_KEY, theme);
  applyThemeToDocument(theme);
  window.dispatchEvent(new CustomEvent(THEME_CHANGED, { detail: { theme } }));
}

/** Rotates light → dark → system → light. */
export function cycleThemePreference(current: ThemePreference): ThemePreference {
  if (current === "light") return "dark";
  if (current === "dark") return "system";
  return "light";
}
